import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, ThumbsUp, ThumbsDown } from "lucide-react";
import { CATEGORY_IMAGES, CATEGORY_PILL_COLORS } from "@/data/curatedContent";

interface Idea {
  title: string;
  description: string;
  initial_goal: string;
  category: string;
  tags: string[];
  difficulty: string;
  why: string;
  problem?: string;
  approach?: string;
  example_output?: string;
  effort_note?: string;
  time_saved?: string;
  who_uses_this?: string;
}

const DIFFICULTY_LABELS: Record<string, { label: string; className: string }> = {
  beginner: { label: "Easy Win", className: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30" },
  intermediate: { label: "Intermediate", className: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
  advanced: { label: "Advanced", className: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30" },
};

interface IdeaFeedItemProps {
  idea: Idea;
  feedback?: "up" | "down";
  socialProof?: string | null;
  onClick: () => void;
  onFeedback: (fb: "up" | "down", e?: React.MouseEvent) => void;
  onBuild: (e: React.MouseEvent) => void;
}

export function IdeaFeedItem({ idea, feedback, socialProof, onClick, onFeedback, onBuild }: IdeaFeedItemProps) {
  const img = CATEGORY_IMAGES[idea.category];
  const pill = CATEGORY_PILL_COLORS[idea.category];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      className="flex flex-col sm:flex-row border-b border-border cursor-pointer hover:bg-muted/30 transition-colors"
      style={{ gap: 24, padding: "32px 0" }}
    >
      {/* Thumbnail */}
      {img && (
        <div
          className="shrink-0 overflow-hidden order-first sm:order-last w-full sm:w-[200px] h-[180px] sm:h-[120px] rounded-lg sm:rounded-md"
        >
          <img
            src={img}
            alt={idea.category}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 8 }}>
          {pill && (
            <Badge variant="secondary" className={`text-xs ${pill.bg} ${pill.text} ${pill.border}`}>
              {idea.category}
            </Badge>
          )}
          {DIFFICULTY_LABELS[idea.difficulty] && (
            <Badge variant="secondary" className={`text-xs ${DIFFICULTY_LABELS[idea.difficulty].className}`}>
              {DIFFICULTY_LABELS[idea.difficulty].label}
            </Badge>
          )}
          {idea.time_saved && (
            <span className="text-muted-foreground" style={{ fontSize: 13 }}>
              ⏱ {idea.time_saved}
            </span>
          )}
        </div>

        <h3
          className="text-foreground font-semibold"
          style={{ fontSize: 22, lineHeight: "30px", marginBottom: 8 }}
        >
          {idea.title}
        </h3>
        <p
          className="text-foreground/80 line-clamp-2"
          style={{ fontSize: 16, lineHeight: "24px", marginBottom: 12 }}
        >
          {idea.description}
        </p>

        <div className="flex items-center" style={{ gap: 20, fontSize: 14 }}>
          <button
            onClick={(e) => { e.stopPropagation(); onBuild(e); }}
            className="text-primary hover:underline font-medium flex items-center gap-1"
          >
            Build <ArrowRight className="h-3 w-3" />
          </button>
          {socialProof && (
            <span className="text-muted-foreground text-xs truncate">{socialProof}</span>
          )}
          <div className="flex items-center gap-1 ml-auto">
            <Button
              size="sm"
              variant="ghost"
              className={`h-7 w-7 p-0 ${feedback === "down" ? "text-red-600 dark:text-red-400" : "text-muted-foreground/40 hover:text-red-600 dark:hover:text-red-400"}`}
              onClick={(e) => { e.stopPropagation(); onFeedback("down", e); }}
            >
              <ThumbsDown className={`h-3.5 w-3.5 ${feedback === "down" ? "fill-current" : ""}`} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className={`h-7 w-7 p-0 ${feedback === "up" ? "text-green-600 dark:text-green-400" : "text-muted-foreground/40 hover:text-green-600 dark:hover:text-green-400"}`}
              onClick={(e) => { e.stopPropagation(); onFeedback("up", e); }}
            >
              <ThumbsUp className={`h-3.5 w-3.5 ${feedback === "up" ? "fill-current" : ""}`} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
